// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { FC, useMemo, useEffect } from "react";

import { Row, Select } from "@hightouch/htui";
import { v4 as uuidv4 } from "uuid";

import { useFormErrorContext } from "src/contexts/form-error-context";
import {
  JourneySetCondition,
  journeySetOperatorOptions,
  journeySetWindowTimestampOperatorOptions,
  RelativeDirection,
  TimestampOperator,
  IntervalUnit,
  TimeType,
  TimestampGranularity,
} from "src/types/visual";

import { FilterPopover } from "src/components/explore/filter-popover";
import { FilterProps } from "./condition";
import { HStack } from "./shared";
import {
  validateJourneySetCondition,
  validateWindowCondition,
} from "./condition-validation";
import { ErrorMessage } from "./error-message";
import { TimestampInput, formatValue } from "./timestamp-input";
import { ExternalLink } from "src/components/links/external-link";
import { Filter } from "./filter";
import { captureException } from "src/lib/monitoring";
import { getJourneySetWindowDisplayValue } from "./utils";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";

const DefaultValueForOperator = (operator: TimestampOperator) => {
  switch (operator) {
    case TimestampOperator.Within:
    case TimestampOperator.NotWithin:
      return {
        value: {
          interval: IntervalUnit.Day,
          quantity: 5,
          direction: RelativeDirection.Backward,
        },
      };
    case TimestampOperator.Between:
    case TimestampOperator.BetweenInclusive:
      return {
        timeType: TimeType.Relative,
        value: {
          before: {
            interval: IntervalUnit.Month,
            quantity: 1,
            direction: RelativeDirection.Backward,
          },
          after: {
            interval: IntervalUnit.Day,
            quantity: 5,
            direction: RelativeDirection.Forward,
          },
        },
      };
    default:
      return {
        value: formatValue(new Date()),
        propertyOptions: { timestampGranularity: TimestampGranularity.Full },
        timeType: TimeType.Absolute,
      };
  }
};

const isTimestampOperator = (value: string): value is TimestampOperator => {
  return Object.values(TimestampOperator).includes(value as TimestampOperator);
};

export const JourneySetFilter: FC<
  Readonly<FilterProps<JourneySetCondition>>
> = ({ condition, onUpdate, ...props }) => {
  const filterId = useMemo<string>(uuidv4, []);
  const windowFilterId = useMemo<string>(uuidv4, []);

  const { realtimeConditionScope } = useQueryBuilderContext();
  const isRealtimeAudience = realtimeConditionScope === "audience";

  const { getErrors, setFieldError, removeErrors } = useFormErrorContext();

  const filterErrors = getErrors(filterId, { allErrors: true });
  const windowFilterErrors = getErrors(windowFilterId);
  const windowQuantityError = windowFilterErrors?.quantity;
  const windowValueError = windowFilterErrors?.value;
  const conditionError = filterErrors?.condition;
  const hasErrors =
    Boolean(filterErrors?.journeyIds) ||
    Boolean(filterErrors?.filter) ||
    Boolean(conditionError);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setFieldError(
      filterId,
      validateJourneySetCondition(condition, {
        isRealtimeAudience,
      }),
    );

    return () => {
      removeErrors([filterId]);
    };
  }, [condition.journeyIds, filterId, isRealtimeAudience]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setFieldError(
      windowFilterId,
      validateWindowCondition(condition.filter.evalWindow),
    );

    return () => {
      removeErrors([windowFilterId]);
    };
  }, [condition.filter.evalWindow, windowFilterId]);

  return (
    <HStack
      gap={2}
      sx={{ alignItems: "flex-start", button: { fontWeight: "medium" } }}
    >
      <Select
        isDisabled={props.isDisabled}
        isInvalid={hasErrors}
        options={journeySetOperatorOptions}
        value={condition.filter.condition}
        width="auto"
        onChange={(value) =>
          onUpdate((condition) => {
            condition.filter = {
              condition: value ?? "in-journey",
              evalWindow: undefined,
            };
          })
        }
      />
      <FilterPopover
        {...props}
        condition={condition}
        hasError={hasErrors}
        onChange={(newCondition) => onUpdate(() => newCondition)}
      />

      {condition.filter.condition !== "in-journey" &&
        condition.filter.condition !== "not-in-journey" && (
          <Filter
            isEmpty={false}
            isDisabled={props.isDisabled}
            error={windowQuantityError || windowValueError}
            content={
              <Row gap={2}>
                <Select
                  removePortal
                  isDisabled={props.isDisabled}
                  options={journeySetWindowTimestampOperatorOptions}
                  value={condition.filter.evalWindow?.operator ?? "any_time"}
                  width="auto"
                  onChange={(operator) => {
                    const conditionType = condition.filter.condition;
                    if (
                      conditionType === "in-journey" ||
                      conditionType === "not-in-journey"
                    ) {
                      captureException(
                        new Error(
                          `Unexpected condition type for timestamp operator: ${conditionType}`,
                        ),
                      );
                      return;
                    }
                    if (operator === "any_time") {
                      onUpdate((condition) => {
                        condition.filter = {
                          condition: conditionType,
                          evalWindow: undefined,
                        };
                      });
                    } else if (operator && isTimestampOperator(operator)) {
                      onUpdate((condition) => {
                        condition.filter = {
                          condition: conditionType,
                          evalWindow: {
                            operator: operator ?? TimestampOperator.Before,
                            ...DefaultValueForOperator(
                              operator ?? TimestampOperator.Before,
                            ),
                          },
                        };
                      });
                    }
                  }}
                />
                {condition.filter.evalWindow && (
                  <TimestampInput
                    isDisabled={props.isDisabled}
                    condition={condition.filter.evalWindow}
                    error={windowQuantityError || windowValueError}
                    hideTime={false}
                    showRelativeDirections
                    allowPropertyComparison={false}
                    onUpdate={(updater) =>
                      onUpdate((condition) => {
                        const prevEvalWindow = condition.filter.evalWindow;
                        if (!prevEvalWindow) {
                          captureException(
                            new Error("evalWindow is unexpectedly undefined"),
                          );
                          return;
                        }
                        const operator = prevEvalWindow.operator;
                        const result = updater(prevEvalWindow);
                        prevEvalWindow.operator = operator;
                        if (result) {
                          condition.filter.evalWindow = { ...result, operator };
                        }
                      })
                    }
                  />
                )}
              </Row>
            }
          >
            {getJourneySetWindowDisplayValue(condition)}
          </Filter>
        )}

      <ExternalLink
        tooltipMessage="Open Journey"
        url={`/journeys/${condition.journeyIds[0]}`}
      />

      {hasErrors && (
        <ErrorMessage>
          {conditionError || filterErrors?.journeyIds || filterErrors?.filter}
        </ErrorMessage>
      )}
    </HStack>
  );
};
